import produce from "immer";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchOfferCalculations,
  updateOfferCalculation,
  createOfferCalculation,
  deleteOfferCalculation,
  fetchOfferCalculation,
} from "../api/offer-calculation";

const initialState = {
  isLoading: false,
  error: false,
  errors: null,
  data: [],
  calculation: {},
  totalCount: 0,
  currentCount: 0,
};

export const initialController = {
  filters: [],
  page: 1,
  per_page: 50,
  query: "",
  sort: "desc",
  sortBy: "created_at",
  active: null,
};

export const deleteOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/deleteOfferCalculationAsync",
  async (payload) => {
    let response = await deleteOfferCalculation(payload);
    if (response.error) return false;
    return response;
  }
);

export const createOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/createOfferCalculationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      let response = await createOfferCalculation(payload);
      // Check if response has error status
      if (response.response?.status && response.response.status >= 400) {
        return rejectWithValue(response);
      }
      // Check if response itself is an error
      if (response.error || (response.errors && Array.isArray(response.errors) && response.errors.length > 0)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response || error);
    }
  }
);

export const updateOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/updateOfferCalculationAsync",
  async (payload, { rejectWithValue }) => {
    try {
      let response = await updateOfferCalculation(payload);
      // Check if response has error status
      if (response.response?.status && response.response.status >= 400) {
        return rejectWithValue(response);
      }
      // Check if response itself is an error
      if (response.error || (response.errors && Array.isArray(response.errors) && response.errors.length > 0)) {
        return rejectWithValue(response);
      }
      return response;
    } catch (error) {
      return rejectWithValue(error.response || error);
    }
  }
);

export const getOfferCalculationsAsync = createAsyncThunk(
  "offerCalculations/getOfferCalculationsAsync",
  async (controller = initialController, { rejectWithValue }) => {
    let response = await fetchOfferCalculations(controller);
    if (response.response?.status?.match(/404|500|401/))
      return rejectWithValue(true);
    return response;
  }
);

export const getOfferCalculationAsync = createAsyncThunk(
  "offerCalculations/getOfferCalculationAsync",
  async (id, { rejectWithValue }) => {
    let response = await fetchOfferCalculation(id);
    if (response.error) return false;
    return response;
  }
);

const offerCalculationSlice = createSlice({
  name: "offerCalculations",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getOfferCalculationsAsync.pending, (state, action) => {
        state.isLoading = true;
      })
      .addCase(getOfferCalculationsAsync.fulfilled, (state, action) => {
        state.isLoading = false;
        state.data = action.payload.data;
        state.totalCount = action.payload.totalCount;
        state.currentCount = action.payload.currentCount;
      })
      .addCase(getOfferCalculationsAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = [
          "Something went wrong when trying to fetch Offer Calculations....is controller missing?",
        ];
      })
      .addCase(updateOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.calculation = action.payload;
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(updateOfferCalculationAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to update Offer Calculation."];
      })
      .addCase(createOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data.push(action.payload);
          draftState.calculation = action.payload; // Set the created calculation as current
          draftState.isLoading = false;
          draftState.error = false;
          draftState.errors = null;
        });
      })
      .addCase(createOfferCalculationAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to create Offer Calculation."];
      })
      .addCase(deleteOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deleteOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.data = action.payload.data;
          draftState.isLoading = false;
        });
      })
      .addCase(deleteOfferCalculationAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to delete Offer Calculation."];
      })
      .addCase(getOfferCalculationAsync.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getOfferCalculationAsync.fulfilled, (state, action) => {
        return produce(state, (draftState) => {
          draftState.calculation = action.payload;
          draftState.isLoading = false;
        });
      })
      .addCase(getOfferCalculationAsync.rejected, (state) => {
        state.isLoading = false;
        state.error = true;
        state.errors = ["Something went wrong when trying to fetch Offer Calculation."];
      });
  },
});

export const { reducer } = offerCalculationSlice;

export default offerCalculationSlice;

export const selectOfferCalculationById = (state, calculationId) =>
  state.offerCalculations.data.find((calc) => calc.id == calculationId);



